/**
 * WebSocket connection management for WeatherFlow Tempest
 * Includes automatic reconnection with exponential backoff
 */

import { WS_URL } from '../config.js';
import {
  getAccessToken,
  getDeviceID,
  getWsid,
  getSocket,
  setSocket,
  addWindOb,
  setLastCheck,
  getChart,
  setCurrentTempF,
  setCurrentTempC,
} from './state.js';
import { windmsTomph, precipmmToin, tempCtoF, kmToMi } from '../utils/conversions.js';
import { processLightning } from './weather.js';
import { updateHealthStatus } from './updates.js';
import { checkAndUpdateSummary } from './records.js';
import { updatePrecipBar } from './charts.js';

// Reconnection configuration
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const RECONNECT_DECAY = 1.5; // Exponential backoff multiplier
const MAX_RECONNECT_ATTEMPTS = 10; // Give up after this many attempts

let reconnectAttempts = 0;
let reconnectDelay = INITIAL_RECONNECT_DELAY;

// Lightning strike batching
const LIGHTNING_FLUSH_INTERVAL = 10000; // 10 seconds
let pendingStrikes = 0;
let lightningFlushTimer = null;
let reconnectTimeout = null;
let isIntentionallyClosed = false;

// Wind bar dynamic scaling
const WIND_BAR_DEFAULT_MAX = 10;
const WIND_BAR_SCALE_STEPS = [10, 15, 20, 25, 30];
const WIND_BAR_EXTEND_THRESHOLD = 3; // Number of hits at max to trigger extension
const WIND_BAR_REDUCE_COUNT = 60; // Observations below threshold to reduce (about 1 min at 1/sec)
let windBarMax = WIND_BAR_DEFAULT_MAX;
let windBarHitsAtMax = 0;
let windBarObsBelowMax = 0;

/**
 * Start WebSocket connection
 * @returns {WebSocket} The socket instance
 */
export function startWebSocket() {
  const accessToken = getAccessToken();
  if (!accessToken) {
    console.error('[WebSocket] Cannot start: accessToken not loaded');
    return null;
  }

  const socket = new WebSocket(`${WS_URL}?token=${accessToken}`);
  setSocket(socket);
  setupWebSocketHandlers();
  return socket;
}

/**
 * Set up WebSocket event handlers
 */
function setupWebSocketHandlers() {
  const socket = getSocket();
  const deviceID = getDeviceID();
  const wsid = getWsid();

  socket.onopen = () => {
    // Reset reconnection state on successful connection
    reconnectAttempts = 0;
    reconnectDelay = INITIAL_RECONNECT_DELAY;
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    const listenStartMessage = {
      type: 'listen_start',
      device_id: deviceID,
      id: `listen_start_${wsid}`,
    };
    socket.send(JSON.stringify(listenStartMessage));

    jQuery('#wsStatusWeather').removeClass('alert').addClass('success');

    const listenStartRapidMessage = {
      type: 'listen_rapid_start',
      device_id: deviceID,
      id: `listen_rapid_start_${wsid}`,
    };
    socket.send(JSON.stringify(listenStartRapidMessage));

    jQuery('#wsStatusTempest').removeClass('alert').addClass('success');
    jQuery('#wsUpdateTempest').text('Online');

    // Update overall health status
    updateHealthStatus();
  };

  socket.onmessage = (event) => {
    if (event.data === 'pong') {
      return;
    }

    const message = JSON.parse(event.data);
    handleWebSocketMessage(message);

    setLastCheck('Weather', new Date());
    jQuery('#wsUpdateWeather').text(new Date().toLocaleTimeString());
  };

  socket.onclose = () => {
    // Update UI to show disconnected state
    jQuery('#wsStatusWeather').removeClass('success').addClass('alert');
    jQuery('#wsStatusTempest').removeClass('success').addClass('alert');
    jQuery('#wsUpdateTempest').text('Offline');

    // Update overall health status
    updateHealthStatus();

    // Attempt reconnection if not intentionally closed
    if (!isIntentionallyClosed) {
      attemptReconnection();
    }
  };

  socket.onerror = (error) => {
    jQuery('#wsStatusWeather').removeClass('success').addClass('alert');
    jQuery('#wsStatusTempest').removeClass('success').addClass('alert');
    console.error('[WebSocket] Error:', error);

    // Update overall health status
    updateHealthStatus();
  };
}

/**
 * Handle incoming WebSocket messages
 * @param {object} message - Parsed WebSocket message
 */
function handleWebSocketMessage(message) {
  switch (message.type) {
  case 'rapid_wind':
    handleRapidWind(message);
    break;
  case 'evt_strike':
    handleLightningStrike(message);
    break;
  case 'obs_st':
    handleObservation(message);
    break;
  }
}

/**
 * Handle rapid wind updates
 * @param {object} message - Wind message from WebSocket
 */
function handleRapidWind(message) {
  const wval = windmsTomph(message.ob[1]);
  const timestamp = message.ob[0];  // Unix timestamp of observation

  // Track wind observations
  addWindOb(wval);

  // Update wind bar display
  updateWindBar(wval);

  // Check if this wind speed exceeds the daily summary max
  checkAndUpdateSummary('wind', wval, timestamp);
}

/**
 * Update the wind bar display with dynamic scaling
 * @param {number} speed - Current wind speed in mph
 */
function updateWindBar(speed) {
  // Dynamic scaling logic
  updateWindBarScale(speed);

  const speedEl = document.getElementById('wind-bar-speed');
  const fillEl = document.getElementById('wind-bar-fill');

  if (speedEl) {
    speedEl.textContent = Math.round(speed);
  }

  if (fillEl) {
    const heightPct = Math.min((speed / windBarMax) * 100, 100);
    fillEl.style.height = `${heightPct}%`;

    // Color based on percentage of current max
    const pct = speed / windBarMax;
    if (pct < 0.4) {
      fillEl.style.backgroundColor = '#4CAF50';
    } else if (pct < 0.7) {
      fillEl.style.backgroundColor = '#8BC34A';
    } else if (pct < 0.9) {
      fillEl.style.backgroundColor = '#FFC107';
    } else {
      fillEl.style.backgroundColor = '#FF9800';
    }
  }
}

/**
 * Update wind bar scale based on recent observations
 * Extends range when frequently hitting max, reduces when calm
 * @param {number} speed - Current wind speed in mph
 */
function updateWindBarScale(speed) {
  const currentStepIndex = WIND_BAR_SCALE_STEPS.indexOf(windBarMax);

  // Check if speed is at or near the current max (within 90%)
  if (speed >= windBarMax * 0.9) {
    windBarHitsAtMax++;
    windBarObsBelowMax = 0;

    // Extend range if we've hit the max enough times
    if (windBarHitsAtMax >= WIND_BAR_EXTEND_THRESHOLD && currentStepIndex < WIND_BAR_SCALE_STEPS.length - 1) {
      windBarMax = WIND_BAR_SCALE_STEPS[currentStepIndex + 1];
      windBarHitsAtMax = 0;
    }
  } else if (speed < windBarMax * 0.5) {
    // Wind is well below the current max
    windBarObsBelowMax++;
    windBarHitsAtMax = Math.max(0, windBarHitsAtMax - 1); // Decay hits slowly

    // Reduce range if wind has been calm for a while
    if (windBarObsBelowMax >= WIND_BAR_REDUCE_COUNT && currentStepIndex > 0) {
      // Only reduce if current speed would fit in the lower range
      const lowerMax = WIND_BAR_SCALE_STEPS[currentStepIndex - 1];
      if (speed < lowerMax * 0.7) {
        windBarMax = lowerMax;
        windBarObsBelowMax = 0;
      }
    }
  } else {
    // Speed is moderate - slowly decay the counters
    windBarHitsAtMax = Math.max(0, windBarHitsAtMax - 1);
    windBarObsBelowMax = Math.max(0, windBarObsBelowMax - 1);
  }
}

/**
 * Handle lightning strike events
 * @param {object} message - Lightning message from WebSocket
 */
function handleLightningStrike(message) {
  const distance = kmToMi(message.evt[1]);
  const date = message.evt[0];
  processLightning(distance, date);

  // Batch strikes and update UI immediately
  pendingStrikes++;
  const currentDisplay = parseInt(jQuery('#lightning-strikes-day').text()) || 0;
  jQuery('#lightning-strikes-day').text(currentDisplay + 1);

  // Start flush timer if not already running
  if (!lightningFlushTimer) {
    lightningFlushTimer = setTimeout(flushLightningStrikes, LIGHTNING_FLUSH_INTERVAL);
  }
}

/**
 * Flush pending lightning strikes to backend
 */
function flushLightningStrikes() {
  lightningFlushTimer = null;

  if (pendingStrikes === 0) {
    return;
  }

  const strikesToSend = pendingStrikes;
  pendingStrikes = 0;

  fetch('/internal/lightning/strike', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ count: strikesToSend }),
  })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Sync UI with server's authoritative count
        jQuery('#lightning-strikes-day').text(data.count);
      }
    })
    .catch(error => console.error('Failed to record lightning strikes:', error));
}

/**
 * Handle station observations
 * @param {object} message - Observation message from WebSocket
 */
function handleObservation(message) {
  const timestamp = message.obs[0][0];  // Unix timestamp of observation
  const obsT = parseFloat(message.obs[0][7]);
  const tempF = Math.round(tempCtoF(obsT));

  // Update precipitation bar
  const precipIn = precipmmToin(message.obs[0][18]);
  updatePrecipBar(precipIn);

  // Update precipitation duration chart
  const chartPrecipDuration = getChart('precipDuration');
  if (chartPrecipDuration) {
    const point = chartPrecipDuration.series[0].points[0];
    point.update(parseInt(message.summary.precip_minutes_local_day));
  }

  // Update UV chart
  const chartUV = getChart('uv');
  if (chartUV) {
    const point = chartUV.series[0].points[0];
    point.update(parseInt(message.obs[0][10]));
  }

  // Update dew point and temperature chart
  const chartDP = getChart('dp');
  if (chartDP) {
    // Update dew point
    const dpPoint = chartDP.series[1].points[0];
    const dpv = Math.round(tempCtoF(parseFloat(message.summary.dew_point)));
    dpPoint.update(dpv);

    // Update temperature
    const tempPoint = chartDP.series[0].points[0];
    tempPoint.update(tempF);
  }

  // Update temperature display and store in state
  jQuery('#obs-temp-f').text(tempF);
  jQuery('#obs-temp-c').text(Math.round(obsT));
  setCurrentTempF(tempF);
  setCurrentTempC(Math.round(obsT));

  // Update lightning strike counts
  jQuery('#lightning-strikes-1hr').text(message.summary.strike_count_1h);
  jQuery('#lightning-strikes-3hr').text(message.summary.strike_count_3h);

  // Update lightning alert if empty
  if (jQuery('#lightning-alert-distance').text() === '') {
    processLightning(
      kmToMi(message.summary.strike_last_dist),
      message.summary.strike_last_epoch,
    );
  }

  // Check if observations exceed daily summary values
  checkAndUpdateSummary('temperature', tempF, timestamp);
  checkAndUpdateSummary('precipitation', precipIn, timestamp);
}

/**
 * Attempt to reconnect with exponential backoff
 */
function attemptReconnection() {
  // Check if we've exceeded max attempts
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error(`[WebSocket] Reconnection failed after ${MAX_RECONNECT_ATTEMPTS} attempts`);
    jQuery('#wsUpdateTempest').text('Connection Failed');
    return;
  }

  reconnectAttempts++;

  // Update UI to show reconnecting state
  jQuery('#wsUpdateTempest').text(`Reconnecting... (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);

  // Schedule reconnection
  reconnectTimeout = setTimeout(() => {
    startWebSocket();

    // Increase delay for next attempt (exponential backoff)
    reconnectDelay = Math.min(reconnectDelay * RECONNECT_DECAY, MAX_RECONNECT_DELAY);
  }, reconnectDelay);
}

/**
 * Stop WebSocket listeners (call on page unload)
 */
export function stopWebSocket() {
  const socket = getSocket();
  const deviceID = getDeviceID();
  const wsid = getWsid();

  if (!socket) {
    return;
  }

  // Mark as intentionally closed to prevent reconnection
  isIntentionallyClosed = true;

  // Clear any pending reconnection
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }

  const listenStopRapidMessage = {
    type: 'listen_rapid_stop',
    device_id: deviceID,
    id: `listen_rapid_stop_${wsid}`,
  };
  socket.send(JSON.stringify(listenStopRapidMessage));

  const listenStopMessage = {
    type: 'listen_stop',
    device_id: deviceID,
    id: `listen_stop_${wsid}`,
  };
  socket.send(JSON.stringify(listenStopMessage));

  // Close the socket
  socket.close();
}

// Register cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', stopWebSocket);
}
